import React, { useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	MenuItem,
	Pagination,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useHistory } from 'react-router-dom';
import { ISort } from 'features/common/types';
import {
	IProviderPayableLinePaymentListFilters,
	IProviderPayableLinePaymentListItem,
	ProviderPayableLinePaymentTypeEnum,
	ProviderPayableLineTypeEnum,
} from 'features/salesOrder/types';
import { getProviderPayableLinePaymentList } from 'features/salesOrder/services';
import { ProviderPayablesPaymentsFilters } from './ProviderPayablesPaymentsFilters';
import { ProviderPayablesPaymentsSummary } from './ProviderPayablesPaymentsSummary';
import routes from 'constants/routes';
import { openPaymentsUrl } from 'pages/SalesOrder/SalesOrderScreen/helpers';
import { extractErrorMessage } from '../../../quotation/helpers';

const PAGE_SIZE_OPTIONS = [50, 100, 150, 200];
const DEFAULT_PAGE_SIZE = 100;
const CRM_DEAL_URL = 'https://crm.mevuelo.com/dashboard/cases/';

const PAYMENT_TYPE_LABELS: Record<ProviderPayableLinePaymentTypeEnum, string> = {
	[ProviderPayableLinePaymentTypeEnum.CorporateCard]: 'Tarjeta Corp.',
	[ProviderPayableLinePaymentTypeEnum.C2A]: 'C2A',
	[ProviderPayableLinePaymentTypeEnum.BankTransfer]: 'Transferencia',
	[ProviderPayableLinePaymentTypeEnum.Check]: 'Cheque',
	[ProviderPayableLinePaymentTypeEnum.Cash]: 'Efectivo',
	[ProviderPayableLinePaymentTypeEnum.Other]: 'Otro',
};

const PAYMENT_TYPE_COLOR: Record<
	ProviderPayableLinePaymentTypeEnum,
	'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
> = {
	[ProviderPayableLinePaymentTypeEnum.CorporateCard]: 'secondary',
	[ProviderPayableLinePaymentTypeEnum.C2A]: 'primary',
	[ProviderPayableLinePaymentTypeEnum.BankTransfer]: 'info',
	[ProviderPayableLinePaymentTypeEnum.Check]: 'default',
	[ProviderPayableLinePaymentTypeEnum.Cash]: 'success',
	[ProviderPayableLinePaymentTypeEnum.Other]: 'default',
};

const TYPE_LABEL: Record<ProviderPayableLineTypeEnum, string> = {
	[ProviderPayableLineTypeEnum.Service]: 'Servicio',
	[ProviderPayableLineTypeEnum.FlightTicket]: 'Ticket vuelo',
	[ProviderPayableLineTypeEnum.FlightAncillaryTicket]: 'Ancillary',
};

const fmtUSD = (n: number): string => n.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CopyButton = ({ value }: { value: string }) => {
	const [copied, setCopied] = useState(false);
	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(value).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	};
	return (
		<Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
			<IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, p: 0.25 }}>
				<ContentCopyIcon sx={{ fontSize: 14, color: copied ? 'success.main' : 'action.active' }} />
			</IconButton>
		</Tooltip>
	);
};

interface ProviderPayablesPaymentsListProps {
	fixedProviderId?: string;
}

const buildFixedProviderPaymentFilters = (fixedProviderId?: string): IProviderPayableLinePaymentListFilters =>
	fixedProviderId ? { providerIds: [fixedProviderId] } : {};

export const ProviderPayablesPaymentsList = (
	{ fixedProviderId }: ProviderPayablesPaymentsListProps = {} as ProviderPayablesPaymentsListProps,
): JSX.Element => {
	const history = useHistory();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [items, setItems] = useState<IProviderPayableLinePaymentListItem[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSize, setCurrentSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageTotal, setPageTotal] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
	const [sortFields] = useState<ISort[]>([{ field: 'paymentDate', order: 'desc' }]);
	const [filters, setFilters] = useState<IProviderPayableLinePaymentListFilters>(() =>
		buildFixedProviderPaymentFilters(fixedProviderId),
	);

	const loadData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const res = await getProviderPayableLinePaymentList(currentPage, currentSize, sortFields, filters);
			setItems(res.data.elements ?? []);
			setPageTotal(Math.ceil(res.data.total / currentSize));
			setTotalCount(res.data.total);
			setTotalAmount(res.data.totalAmount);
		} catch (error) {
			console.log(error);
			setError(extractErrorMessage(error, 'No se pudo cargar el listado. Intentá de nuevo.'));
		} finally {
			setLoading(false);
		}
	}, [currentPage, currentSize, sortFields, filters]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const mergeWithFixedProvider = useCallback(
		(newFilters: IProviderPayableLinePaymentListFilters): IProviderPayableLinePaymentListFilters => ({
			...newFilters,
			...(fixedProviderId ? { providerIds: [fixedProviderId] } : {}),
		}),
		[fixedProviderId],
	);

	const handleFiltersChange = useCallback(
		(newFilters: IProviderPayableLinePaymentListFilters) => {
			setFilters(mergeWithFixedProvider(newFilters));
			setCurrentPage(1);
		},
		[mergeWithFixedProvider],
	);

	const handleClearFilters = useCallback(() => {
		setFilters(buildFixedProviderPaymentFilters(fixedProviderId));
		setCurrentPage(1);
	}, [fixedProviderId]);

	const handleRowClick = (item: IProviderPayableLinePaymentListItem, event: React.MouseEvent) => {
		openPaymentsUrl(history, item.saleOrderId, item.providerPayableLineId, event);
	};

	return (
		<Box>
			<ProviderPayablesPaymentsFilters
				onFiltersChange={handleFiltersChange}
				onClearFilters={handleClearFilters}
				hideProviderFilter={!!fixedProviderId}
				embedded={!!fixedProviderId}
			/>

			<ProviderPayablesPaymentsSummary totalCount={totalCount} totalAmount={totalAmount} loading={loading} />

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Box display="flex" justifyContent="center" py={6}>
					<CircularProgress />
				</Box>
			) : (
				<>
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow sx={{ bgcolor: 'grey.50' }}>
									<TableCell>Fecha pago</TableCell>
									<TableCell>Proveedor</TableCell>
									<TableCell>Tipo</TableCell>
									<TableCell>Ticket / Localizador</TableCell>
									<TableCell>Pasajero</TableCell>
									<TableCell>Tipo pago</TableCell>
									<TableCell align="right">Monto (USD)</TableCell>
									<TableCell>Moneda local</TableCell>
									<TableCell align="right">TC</TableCell>
									<TableCell>Nota</TableCell>
									<TableCell>Observación</TableCell>
									<TableCell>Orden de venta</TableCell>
									<TableCell>Deal</TableCell>
									<TableCell align="center">Mercado</TableCell>
									<TableCell>Registrado por</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{items.length === 0 ? (
									<TableRow>
										<TableCell colSpan={15} align="center" sx={{ py: 4, color: 'text.secondary' }}>
											No hay resultados para los filtros aplicados.
										</TableCell>
									</TableRow>
								) : (
									items.map((item) => (
										<TableRow
											key={item.id}
											hover
											onClick={(event) => handleRowClick(item, event)}
											sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
										>
											{/* Fecha pago */}
											<TableCell sx={{ whiteSpace: 'nowrap' }}>
												<Typography variant="body2">{item.paymentDate}</Typography>
											</TableCell>

											{/* Proveedor */}
											<TableCell>
												<Typography variant="body2" noWrap>
													{item.providerName ?? item.providerId}
												</Typography>
											</TableCell>

											{/* Tipo de línea */}
											<TableCell>
												<Typography variant="caption" color="text.secondary" noWrap>
													{TYPE_LABEL[item.payableType]}
												</Typography>
											</TableCell>

											{/* Ticket */}
											<TableCell>
												{item.ticket ? (
													<Typography variant="body2" fontFamily="monospace">
														{item.ticket}
													</Typography>
												) : (
													<Typography variant="body2" color="text.secondary">
														—
													</Typography>
												)}
											</TableCell>

											{/* Pasajero */}
											<TableCell>
												<Typography variant="body2" noWrap>
													{item.passengerName ?? '—'}
												</Typography>
											</TableCell>

											{/* Tipo de pago */}
											<TableCell>
												<Chip
													label={PAYMENT_TYPE_LABELS[item.paymentType]}
													color={PAYMENT_TYPE_COLOR[item.paymentType]}
													size="small"
													variant="outlined"
												/>
											</TableCell>

											{/* Monto USD */}
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
												<Typography
													variant="body2"
													fontWeight={600}
													color={item.amount < 0 ? 'error.main' : 'text.primary'}
												>
													{item.amount < 0 ? '−' : ''}USD {fmtUSD(Math.abs(item.amount))}
												</Typography>
											</TableCell>

											{/* Moneda local */}
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{item.localCurrency ?? '—'}
												</Typography>
											</TableCell>

											{/* Tipo de cambio */}
											<TableCell align="right">
												<Typography variant="body2" color="text.secondary">
													{item.exchangeRate !== undefined && item.exchangeRate !== 1
														? item.exchangeRate.toLocaleString('es-UY')
														: '—'}
												</Typography>
											</TableCell>

											{/* Nota */}
											<TableCell sx={{ maxWidth: 130 }}>
												<Tooltip title={item.notes ?? ''} placement="top" disableHoverListener={!item.notes}>
													<Typography variant="body2" noWrap>
														{item.notes ?? '—'}
													</Typography>
												</Tooltip>
											</TableCell>

											{/* Observación */}
											<TableCell sx={{ maxWidth: 130 }}>
												<Tooltip
													title={item.observation ?? ''}
													placement="top"
													disableHoverListener={!item.observation}
												>
													<Typography variant="body2" noWrap>
														{item.observation ?? '—'}
													</Typography>
												</Tooltip>
											</TableCell>

											{/* Orden de venta */}
											<TableCell>
												<Box display="flex" alignItems="center">
													<Tooltip title="Ir a la orden de venta">
														<Typography
															variant="body2"
															color="primary"
															sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
															onClick={(e) => {
																e.stopPropagation();
																history.push(routes.salesOrder.edit.replace(':id', item.saleOrderId));
															}}
														>
															{item.saleOrderId.substring(0, 8)}…
														</Typography>
													</Tooltip>
													<Tooltip title="Abrir en nueva pestaña">
														<IconButton
															size="small"
															sx={{ p: 0.25 }}
															onClick={(e) => {
																e.stopPropagation();
																window.open(
																	`${window.location.origin}${routes.salesOrder.edit.replace(':id', item.saleOrderId)}`,
																	'_blank',
																);
															}}
														>
															<OpenInNewIcon sx={{ fontSize: 14 }} />
														</IconButton>
													</Tooltip>
													<CopyButton value={item.saleOrderId} />
												</Box>
											</TableCell>

											{/* Deal */}
											<TableCell onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
												{!item.isBitrixDeal ? (
													<Box display="flex" alignItems="center">
														<Tooltip title="Abrir en CRM">
															<Typography
																variant="body2"
																color="primary"
																sx={{ cursor: 'pointer' }}
																onClick={(e) => {
																	e.stopPropagation();
																	window.open(`${CRM_DEAL_URL}${item.dealId}`, '_blank', 'noopener,noreferrer');
																}}
															>
																#{item.dealId}
															</Typography>
														</Tooltip>

														<Tooltip title="Abrir en CRM">
															<IconButton
																size="small"
																sx={{ p: 0.25 }}
																onClick={(e) => {
																	e.stopPropagation();
																	window.open(`${CRM_DEAL_URL}${item.dealId}`, '_blank', 'noopener,noreferrer');
																}}
															>
																<OpenInNewIcon sx={{ fontSize: 14 }} />
															</IconButton>
														</Tooltip>

														<Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
															<CopyButton value={String(item.dealId)} />
														</Box>
													</Box>
												) : (
													<Box display="flex" alignItems="center">
														<Tooltip title="Deal de Bitrix">
															<Typography variant="body2" color="primary" sx={{ cursor: 'default' }}>
																#{item.dealId}
															</Typography>
														</Tooltip>

														<Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
															<CopyButton value={String(item.dealId)} />
														</Box>
													</Box>
												)}
											</TableCell>

											{/* Mercado */}
											<TableCell align="center">
												{item.sellerMarket ? (
													<Chip label={item.sellerMarket} size="small" variant="outlined" />
												) : (
													<Typography variant="body2" color="text.secondary">
														—
													</Typography>
												)}
											</TableCell>

											{/* Registrado por */}
											<TableCell>
												<Typography variant="caption" color="text.secondary" noWrap>
													{item.createdBy}
												</Typography>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{/* Paginación */}
					{pageTotal > 0 && (
						<Grid container justifyContent="center" flexDirection="row" padding={2}>
							<Grid item xs={12} md={1} textAlign="center">
								<Select
									value={currentSize}
									size="small"
									onChange={(e) => {
										setCurrentSize(Number(e.target.value));
										setCurrentPage(1);
									}}
								>
									{PAGE_SIZE_OPTIONS.map((v) => (
										<MenuItem key={v} value={v}>
											{v}
										</MenuItem>
									))}
								</Select>
							</Grid>
							<Grid item xs={12} md={6} marginTop={1}>
								<Pagination
									count={pageTotal}
									page={currentPage}
									onChange={(_, page) => setCurrentPage(page)}
									color="primary"
								/>
							</Grid>
						</Grid>
					)}
				</>
			)}
		</Box>
	);
};
